import { hasDepthChannel } from '@zephyr3d/device';
import { LIGHT_TYPE_DIRECTIONAL, LIGHT_TYPE_RECT, LIGHT_TYPE_POINT, LIGHT_TYPE_SPOT } from '../values.js';
import { encodeNormalizedFloatToRGBA, decode2HalfFromRGBA, decodeNormalizedFloatFromRGBA } from './misc.js';
import { ShaderHelper } from '../material/shader/helper.js';
import { getDevice } from '../app/api.js';

function getShadowMapTexelSize(scope) {
    return scope.$builder.div(1, ShaderHelper.getShadowCameraParams(scope).z);
}
function getShadowMapSize(scope) {
    return ShaderHelper.getShadowCameraParams(scope).z;
}
/** @internal */ function computeShadowMapDepth(scope, worldPos, targetFormat) {
    const funcNameComputeShadowMapDepth = 'Z_computeShadowMapDepth';
    const pb = scope.$builder;
    pb.func(funcNameComputeShadowMapDepth, [
        pb.vec3('worldPos')
    ], function() {
        if (hasDepthChannel(targetFormat)) {
            // use native shadowmap
            this.$return(pb.vec4(pb.emulateDepthClamp ? pb.clamp(scope.$inputs.clamppedDepth, 0, 1) : scope.$builtins.fragCoord.z, 0, 0, 1));
        } else {
            this.$l.depth = pb.float();
            this.$l.lightType = ShaderHelper.getLightTypeForShadow(this);
            this.$if(pb.or(pb.equal(this.lightType, LIGHT_TYPE_DIRECTIONAL), pb.equal(this.lightType, LIGHT_TYPE_RECT)), function() {
                this.depth = pb.emulateDepthClamp ? pb.clamp(this.$inputs.clamppedDepth, 0, 1) : this.$builtins.fragCoord.z;
            }).$elseif(pb.equal(this.lightType, LIGHT_TYPE_POINT), function() {
                this.$l.lightSpacePos = pb.mul(ShaderHelper.getLightViewMatrixForShadow(this), pb.vec4(this.worldPos, 1));
                this.depth = pb.clamp(pb.div(pb.length(this.lightSpacePos.xyz), ShaderHelper.getLightPositionAndRangeForShadow(this).w), 0, 1);
            }).$else(function() {
                this.$l.lightSpacePos = pb.mul(ShaderHelper.getLightViewMatrixForShadow(this), pb.vec4(this.worldPos, 1));
                this.depth = pb.clamp(pb.div(pb.neg(this.lightSpacePos.z), ShaderHelper.getLightPositionAndRangeForShadow(this).w), 0, 1);
            });
            this.$return(targetFormat === 'rgba8unorm' ? encodeNormalizedFloatToRGBA(this, this.depth) : pb.vec4(this.depth, 0, 0, 1));
        }
    });
    return pb.getGlobalScope()[funcNameComputeShadowMapDepth](worldPos);
}
/** @internal */ function computeReceiverPlaneDepthBias(scope, texCoord) {
    const funcNameComputeReceiverPlaneDepthBias = 'lib_computeReceiverPlaneDepthBias';
    const pb = scope.$builder;
    pb.func(funcNameComputeReceiverPlaneDepthBias, [
        pb.vec4('coords')
    ], function() {
        this.$l.dx = pb.dpdx(this.coords);
        this.$l.dy = pb.dpdy(this.coords);
        this.$l.biasMultiply = pb.float(1);
        this.$l.uv = pb.vec2(pb.sub(pb.mul(this.dy.y, this.dx.z), pb.mul(this.dx.y, this.dy.z)), pb.sub(pb.mul(this.dx.x, this.dy.z), pb.mul(this.dy.x, this.dx.z)));
        this.$l.uv = pb.mul(this.$l.uv, pb.div(this.biasMultiply, pb.sub(pb.mul(this.dx.x, this.dy.y), pb.mul(this.dx.y, this.dy.x))));
        // from unity shader
        this.$l.minFractionalError = pb.float(0.01);
        this.$l.fractionalSamplingError = pb.dot(pb.vec2(getShadowMapTexelSize(this)), pb.abs(this.$l.uv));
        this.$l.staticBias = pb.min(this.$l.fractionalSamplingError, this.$l.minFractionalError);
        // return
        this.$return(pb.vec3(this.$l.uv, this.$l.staticBias));
    });
    return pb.getGlobalScope()[funcNameComputeReceiverPlaneDepthBias](texCoord);
}
function sampleShadowMapPCF(scope, shadowMapFormat, pos, offset, depth, cascade) {
    const funcName = `${cascade ? 'lib_sampleShadowMapCascadePCF' : 'lib_sampleShadowMapPCF'}_${shadowMapFormat}`;
    const pb = scope.$builder;
    const nativeShadowMap = hasDepthChannel(shadowMapFormat);
    pb.func(funcName, [
        pb.vec2('coords'),
        pb.float('z'),
        pb.vec2('offset'),
        ...cascade ? [
            pb.int('cascade')
        ] : []
    ], function() {
        const sampleDepth = this.z;
        const uv = pb.add(this.coords, this.offset);
        if (nativeShadowMap) {
            this.$return(cascade && getDevice().type !== 'webgl' ? pb.textureArraySampleCompareLevel(ShaderHelper.getShadowMap(this), uv, this.cascade, sampleDepth) : pb.textureSampleCompareLevel(ShaderHelper.getShadowMap(this), uv, sampleDepth));
        } else {
            this.$l.shadowTex = cascade && getDevice().type !== 'webgl' ? pb.textureArraySampleLevel(ShaderHelper.getShadowMap(this), uv, this.cascade, 0) : pb.textureSampleLevel(ShaderHelper.getShadowMap(this), uv, 0);
            if (shadowMapFormat === 'rgba8unorm') {
                this.shadowTex.x = decodeNormalizedFloatFromRGBA(this, this.shadowTex);
            }
            this.$return(pb.step(sampleDepth, this.shadowTex.x));
        }
    });
    return pb.getGlobalScope()[funcName](pos, depth, offset, ...cascade ? [
        cascade
    ] : []);
}
function chebyshevUpperBound(scope, distance, occluder) {
    const funcNameChebyshevUpperBound = 'lib_chebyshevUpperBound';
    const pb = scope.$builder;
    pb.func(funcNameChebyshevUpperBound, [
        pb.float('distance'),
        pb.vec3('occluder')
    ], function() {
        this.$l.shadow = pb.float(1);
        this.$l.coverage = this.occluder.z;
        this.$l.invCoverage = pb.div(1, pb.max(this.coverage, 0.00001));
        this.$l.moments = pb.mul(this.occluder.xy, this.invCoverage);
        this.$l.test = pb.step(this.distance, this.moments.x);
        this.$if(pb.notEqual(this.test, 1), function() {
            this.$l.d = pb.sub(this.distance, this.moments.x);
            this.$l.variance = pb.max(pb.sub(this.moments.y, pb.mul(this.moments.x, this.moments.x)), 0.000002);
            const darkness = ShaderHelper.getDepthBiasValues(this).z;
            this.shadow = pb.div(this.variance, pb.add(this.variance, pb.mul(this.d, this.d)));
            this.shadow = pb.clamp(pb.div(pb.sub(this.shadow, darkness), pb.sub(1, darkness)), 0, 1);
        });
        this.shadow = pb.mix(1, this.shadow, pb.clamp(this.coverage, 0, 1));
        this.$return(this.shadow);
    });
    return pb.getGlobalScope()[funcNameChebyshevUpperBound](distance, occluder);
}
/** @internal */ function filterShadowVSM(scope, lightType, shadowMapFormat, texCoord, cascade) {
    const funcNameFilterShadowVSM = 'lib_filterShadowVSM';
    const pb = scope.$builder;
    pb.func(funcNameFilterShadowVSM, [
        pb.vec4('texCoord'),
        ...cascade ? [
            pb.int('cascade')
        ] : []
    ], function() {
        if (lightType === LIGHT_TYPE_POINT) {
            this.$l.shadowTex = pb.textureSampleLevel(ShaderHelper.getShadowMap(this), this.texCoord.xyz, 0);
            this.$return(chebyshevUpperBound(this, this.texCoord.w, shadowMapFormat === 'rgba8unorm' ? pb.vec3(decode2HalfFromRGBA(this, this.shadowTex), 1) : this.shadowTex.rgb));
        } else {
            if (getDevice().type !== 'webgl' && cascade) {
                this.$l.shadowTex = pb.textureArraySampleLevel(ShaderHelper.getShadowMap(this), this.texCoord.xy, this.cascade, 0);
            } else {
                this.$l.shadowTex = pb.textureSampleLevel(ShaderHelper.getShadowMap(this), this.texCoord.xy, 0);
            }
            this.$return(chebyshevUpperBound(this, this.texCoord.z, shadowMapFormat === 'rgba8unorm' ? pb.vec3(decode2HalfFromRGBA(this, this.shadowTex), 1) : this.shadowTex.rgb));
        }
    });
    return pb.getGlobalScope()[funcNameFilterShadowVSM](texCoord, ...cascade ? [
        cascade
    ] : []);
}
/** @internal */ function filterShadowESM(scope, lightType, shadowMapFormat, logSpace, shadowVertex, cascade, depthBias) {
    const funcNameFilterShadowESM = 'lib_filterShadowESM';
    const pb = scope.$builder;
    pb.func(funcNameFilterShadowESM, [
        lightType === LIGHT_TYPE_POINT ? pb.vec3('shadowVertex') : pb.vec4('shadowVertex'),
        ...cascade ? [
            pb.int('cascade')
        ] : [],
        ...depthBias ? [
            pb.float('depthBias')
        ] : []
    ], function() {
        if (lightType === LIGHT_TYPE_POINT) {
            this.$l.depth = pb.div(pb.length(this.shadowVertex.xyz), ShaderHelper.getLightPositionAndRangeForShadow(this).w);
            this.$l.shadowTex = pb.textureSampleLevel(ShaderHelper.getShadowMap(this), this.shadowVertex.xyz, 0);
            if (shadowMapFormat === 'rgba8unorm') {
                this.shadowTex.x = decodeNormalizedFloatFromRGBA(this, this.shadowTex);
            }
        } else {
            if (cascade && getDevice().type !== 'webgl') {
                this.$l.shadowTex = pb.textureArraySampleLevel(ShaderHelper.getShadowMap(this), this.shadowVertex.xy, this.cascade, 0);
            } else {
                this.$l.shadowTex = pb.textureSampleLevel(ShaderHelper.getShadowMap(this), this.shadowVertex.xy, 0);
            }
            if (shadowMapFormat === 'rgba8unorm') {
                this.shadowTex.x = decodeNormalizedFloatFromRGBA(this, this.shadowTex);
            }
            if (lightType === LIGHT_TYPE_SPOT) {
                this.$l.nearFar = ShaderHelper.getShadowCameraParams(this).xy;
                this.$l.depth = ShaderHelper.nonLinearDepthToLinearNormalized(this, this.shadowVertex.z, this.nearFar);
            } else {
                this.$l.depth = this.shadowVertex.z;
            }
        }
        if (depthBias) {
            this.depth = pb.max(0, pb.sub(this.depth, this.depthBias));
        }
        const depthScale = ShaderHelper.getDepthBiasValues(this).z;
        this.$l.shadow = logSpace ? pb.clamp(pb.exp(pb.min(87, pb.sub(this.shadowTex.x, pb.mul(depthScale, this.depth)))), 0, 1) : pb.clamp(pb.exp(pb.min(87, pb.mul(depthScale, pb.sub(this.shadowTex.x, this.depth)))), 0, 1);
        if (shadowMapFormat !== 'rgba8unorm') {
            this.shadow = pb.mix(1, this.shadow, pb.clamp(this.shadowTex.y, 0, 1));
        }
        this.$return(this.shadow);
    });
    return pb.getGlobalScope()[funcNameFilterShadowESM](shadowVertex, ...cascade ? [
        cascade
    ] : [], ...depthBias ? [
        depthBias
    ] : []);
}
/** @internal */ function filterShadowPCF(scope, lightType, shadowMapFormat, kernelSize, texCoord, receiverPlaneDepthBias, cascade) {
    const funcNameFilterShadowPCF = `lib_filterShadowPCF${kernelSize}x${kernelSize}`;
    const pb = scope.$builder;
    pb.func(funcNameFilterShadowPCF, [
        pb.vec4('texCoord'),
        ...receiverPlaneDepthBias ? [
            pb.vec3('receiverPlaneDepthBias')
        ] : [],
        ...cascade ? [
            pb.int('cascade')
        ] : []
    ], function() {
        this.$l.lightDepth = this.texCoord.z;
        if (receiverPlaneDepthBias) {
            this.lightDepth = pb.sub(this.lightDepth, this.receiverPlaneDepthBias.z);
        }
        const shadowMapTexelSize = getShadowMapTexelSize(this);
        this.$l.uv = pb.add(pb.mul(this.texCoord.xy, pb.vec2(getShadowMapSize(this))), pb.vec2(0));
        this.$l.st = pb.fract(this.uv);
        this.$l.baseUV = pb.sub(pb.floor(this.uv), pb.vec2(0.5));
        this.baseUV = pb.mul(this.baseUV, shadowMapTexelSize);
        this.$l.shadow = pb.float(0);
        if (kernelSize === 3) {
            this.$l.uvw0 = pb.sub(pb.vec2(3), pb.mul(2, this.st));
            this.$l.uvw1 = pb.add(pb.vec2(1), pb.mul(2, this.st));
            this.$l.u = pb.mul(pb.vec2(pb.sub(pb.div(pb.sub(2, this.st.x), this.uvw0.x), 1), pb.add(pb.div(this.st.x, this.uvw1.x), 1)), shadowMapTexelSize);
            this.$l.v = pb.mul(pb.vec2(pb.sub(pb.div(pb.sub(2, this.st.y), this.uvw0.y), 1), pb.add(pb.div(this.st.y, this.uvw1.y), 1)), shadowMapTexelSize);
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.vec2(this.u.x, this.v.x), this.lightDepth, this.cascade), this.uvw0.x, this.uvw0.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.vec2(this.u.y, this.v.x), this.lightDepth, this.cascade), this.uvw1.x, this.uvw0.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.vec2(this.u.x, this.v.y), this.lightDepth, this.cascade), this.uvw0.x, this.uvw1.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.vec2(this.u.y, this.v.y), this.lightDepth, this.cascade), this.uvw1.x, this.uvw1.y));
            this.shadow = pb.div(this.shadow, 16);
        } else if (kernelSize === 5) {
            this.$l.uvw0 = pb.sub(pb.vec2(4), pb.mul(this.st, 3));
            this.$l.uvw1 = pb.vec2(7);
            this.$l.uvw2 = pb.add(pb.vec2(1), pb.mul(this.st, 3));
            this.$l.u = pb.mul(pb.vec3(pb.sub(pb.div(pb.sub(3, pb.mul(this.st.x, 2)), this.uvw0.x), 2), pb.div(pb.add(this.st.x, 3), this.uvw1.x), pb.add(pb.div(this.st.x, this.uvw2.x), 2)), shadowMapTexelSize);
            this.$l.v = pb.mul(pb.vec3(pb.sub(pb.div(pb.sub(3, pb.mul(this.st.y, 2)), this.uvw0.y), 2), pb.div(pb.add(this.st.y, 3), this.uvw1.y), pb.add(pb.div(this.st.y, this.uvw2.y), 2)), shadowMapTexelSize);
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.vec2(this.u.x, this.v.x), this.lightDepth, this.cascade), this.uvw0.x, this.uvw0.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.vec2(this.u.y, this.v.x), this.lightDepth, this.cascade), this.uvw1.x, this.uvw0.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.vec2(this.u.z, this.v.x), this.lightDepth, this.cascade), this.uvw2.x, this.uvw0.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.vec2(this.u.x, this.v.y), this.lightDepth, this.cascade), this.uvw0.x, this.uvw1.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.vec2(this.u.y, this.v.y), this.lightDepth, this.cascade), this.uvw1.x, this.uvw1.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.vec2(this.u.z, this.v.y), this.lightDepth, this.cascade), this.uvw2.x, this.uvw1.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.vec2(this.u.x, this.v.z), this.lightDepth, this.cascade), this.uvw0.x, this.uvw2.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.vec2(this.u.y, this.v.z), this.lightDepth, this.cascade), this.uvw1.x, this.uvw2.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.vec2(this.u.z, this.v.z), this.lightDepth, this.cascade), this.uvw2.x, this.uvw2.y));
            this.shadow = pb.div(this.shadow, 144);
        } else if (kernelSize === 7) {
            this.$l.uvw0 = pb.sub(pb.mul(this.st, 5), pb.vec2(6));
            this.$l.uvw1 = pb.sub(pb.mul(this.st, 11), pb.vec2(28));
            this.$l.uvw2 = pb.sub(pb.mul(this.st, -11), pb.vec2(17));
            this.$l.uvw3 = pb.sub(pb.mul(this.st, -5), 1);
            this.$l.u = pb.vec4(pb.sub(pb.div(pb.sub(pb.mul(this.st.x, 4), 5), this.uvw0.x), 3), pb.sub(pb.div(pb.sub(pb.mul(this.st.x, 4), 16), this.uvw1.x), 1), pb.add(pb.div(pb.sub(pb.mul(this.st.x, -7), 5), this.uvw2.x), 1), pb.add(pb.div(pb.neg(this.st.x), this.uvw3.x), 3));
            this.$l.v = pb.vec4(pb.sub(pb.div(pb.sub(pb.mul(this.st.y, 4), 5), this.uvw0.y), 3), pb.sub(pb.div(pb.sub(pb.mul(this.st.y, 4), 16), this.uvw1.y), 1), pb.add(pb.div(pb.sub(pb.mul(this.st.y, -7), 5), this.uvw2.y), 1), pb.add(pb.div(pb.neg(this.st.y), this.uvw3.y), 3));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.mul(pb.vec2(this.u.x, this.v.x), shadowMapTexelSize), this.lightDepth, this.cascade), this.uvw0.x, this.uvw0.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.mul(pb.vec2(this.u.y, this.v.x), shadowMapTexelSize), this.lightDepth, this.cascade), this.uvw1.x, this.uvw0.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.mul(pb.vec2(this.u.z, this.v.x), shadowMapTexelSize), this.lightDepth, this.cascade), this.uvw2.x, this.uvw0.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.mul(pb.vec2(this.u.w, this.v.x), shadowMapTexelSize), this.lightDepth, this.cascade), this.uvw3.x, this.uvw0.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.mul(pb.vec2(this.u.x, this.v.y), shadowMapTexelSize), this.lightDepth, this.cascade), this.uvw0.x, this.uvw1.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.mul(pb.vec2(this.u.y, this.v.y), shadowMapTexelSize), this.lightDepth, this.cascade), this.uvw1.x, this.uvw1.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.mul(pb.vec2(this.u.z, this.v.y), shadowMapTexelSize), this.lightDepth, this.cascade), this.uvw2.x, this.uvw1.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.mul(pb.vec2(this.u.w, this.v.y), shadowMapTexelSize), this.lightDepth, this.cascade), this.uvw3.x, this.uvw1.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.mul(pb.vec2(this.u.x, this.v.z), shadowMapTexelSize), this.lightDepth, this.cascade), this.uvw0.x, this.uvw2.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.mul(pb.vec2(this.u.y, this.v.z), shadowMapTexelSize), this.lightDepth, this.cascade), this.uvw1.x, this.uvw2.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.mul(pb.vec2(this.u.z, this.v.z), shadowMapTexelSize), this.lightDepth, this.cascade), this.uvw2.x, this.uvw2.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.mul(pb.vec2(this.u.w, this.v.z), shadowMapTexelSize), this.lightDepth, this.cascade), this.uvw3.x, this.uvw2.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.mul(pb.vec2(this.u.x, this.v.w), shadowMapTexelSize), this.lightDepth, this.cascade), this.uvw0.x, this.uvw3.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.mul(pb.vec2(this.u.y, this.v.w), shadowMapTexelSize), this.lightDepth, this.cascade), this.uvw1.x, this.uvw3.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.mul(pb.vec2(this.u.z, this.v.w), shadowMapTexelSize), this.lightDepth, this.cascade), this.uvw2.x, this.uvw3.y));
            this.shadow = pb.add(this.shadow, pb.mul(sampleShadowMapPCF(this, shadowMapFormat, this.baseUV, pb.mul(pb.vec2(this.u.w, this.v.w), shadowMapTexelSize), this.lightDepth, this.cascade), this.uvw3.x, this.uvw3.y));
            this.shadow = pb.div(this.shadow, 2704);
        }
        this.$return(this.shadow);
    });
    return pb.getGlobalScope()[funcNameFilterShadowPCF](texCoord, ...receiverPlaneDepthBias ? [
        receiverPlaneDepthBias
    ] : [], ...cascade ? [
        cascade
    ] : []);
}

export { computeReceiverPlaneDepthBias, computeShadowMapDepth, filterShadowESM, filterShadowPCF, filterShadowVSM };
//# sourceMappingURL=shadow.js.map
